import { useTranslation } from "react-i18next";
import {
  piTotalMinor,
  totalMinor,
  type InvoiceDetailDto,
  type PiDetailDto,
} from "@hisense/shared";
import { Panel, PanelHeader } from "@/components/ui/Panel";
import { TableShell, Td, Th, Tr } from "@/components/ui/Table";
import { formatMoney, formatQty, formatUnitPrice } from "@/domain/money";
import { formatDate } from "@/domain/datetime";
export function InvoiceDocumentView({
  document: d,
}: {
  document: InvoiceDetailDto | PiDetailDto;
}) {
  const { t } = useTranslation();
  const subtotal = d.items.reduce((sum, item) => sum + item.amount, 0);
  const base = totalMinor(subtotal + d.upcharge);
  const less = base - piTotalMinor(subtotal, d.upcharge, d.discount);
  const parties = [
    { label: t("pi.billTo"), name: d.billToName, contact: d.shipTo },
    {
      label: t("pi.notifyPartyCustomer"),
      name: d.notifyPartyName,
      contact: d.notifyPartyContact,
    },
    {
      label: t("pi.consigneeCustomer"),
      name: d.consigneeName,
      contact: d.consigneeSnapshot,
    },
  ];
  return (
    <div className="min-w-0 space-y-3">
      <Panel className="p-4">
        <dl className="grid gap-4 sm:grid-cols-3">
          {parties.map((p) => (
            <div key={p.label} className="min-w-0">
              <dt className="text-2xs uppercase tracking-wide text-ink-faint">
                {p.label}
              </dt>
              <dd className="mt-1 break-words text-sm text-ink">
                <p className="font-medium">{p.name || "—"}</p>
                <p className="mt-1 whitespace-pre-line text-xs text-ink-soft">
                  {p.contact || "—"}
                </p>
              </dd>
            </div>
          ))}
        </dl>
      </Panel>
      <Panel className="p-4">
        <dl className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {[
            [t("pi.poNo"), d.customerPoNo],
            [t("pi.date"), formatDate(d.date)],
            [t("common.delivery"), formatDate(d.deliveryDate)],
            [t("pi.respBy"), d.respBy],
            [t("pi.paymentCompany"), d.paymentCompany],
            [t("customers.terms"), d.paymentTerms],
            [t("common.shipping"), d.shippingMode],
            [t("pi.shippingMarks"), d.shippingMarks],
            [t("pi.customerDept"), d.customerDept],
            [t("pi.portOfLoading"), d.portOfLoading],
            [t("pi.countryOfOrigin"), d.countryOfOrigin],
            [t("lot.pProcess"), t(d.pProcess ? "invoiceWorkspace.pProcessOn" : "invoiceWorkspace.pProcessOff")],
          ].map(([label, value]) => (
            <div key={label} className="min-w-0">
              <dt className="text-2xs uppercase tracking-wide text-ink-faint">
                {label}
              </dt>
              <dd className="mt-1 break-words text-xs text-ink">
                {value || "—"}
              </dd>
            </div>
          ))}
        </dl>
      </Panel>
      <Panel>
        <PanelHeader title={t("pi.lineItems")} />
        <TableShell className="rounded-none border-0">
          <thead>
            <tr>
              <Th>#</Th>
              <Th pin>
                {t("common.code")} / {t("common.subRef")}
              </Th>
              <Th>{t("common.description")}</Th>
              <Th>{t("common.colorSize")}</Th>
              <Th>{t("lot.number")}</Th>
              <Th align="right">{t("pi.qty")}</Th>
              <Th>{t("common.unit")}</Th>
              <Th align="right">{t("pi.unitPrice")}</Th>
              <Th align="right">{t("pi.amount")}</Th>
            </tr>
          </thead>
          <tbody>
            {d.items.map((it, i) => (
              <Tr key={it.lineNo}>
                <Td className="font-mono tabular text-xs text-ink-faint">
                  {i + 1}
                </Td>
                <Td pin className="font-mono tabular text-xs">
                  {it.productCode || "—"}
                  {it.subRef ? ` / ${it.subRef}` : ""}
                </Td>
                <Td>
                  <p className="max-w-72 whitespace-normal text-xs">
                    {it.description || "—"}
                  </p>
                </Td>
                <Td className="text-xs">
                  {[it.color, it.size].filter(Boolean).join(" · ") || "—"}
                </Td>
                <Td className="font-mono tabular text-xs">
                  {it.lotNumber || "—"}
                </Td>
                <Td align="right" className="font-mono tabular">
                  {formatQty(it.quantity)}
                </Td>
                <Td className="text-xs">{it.unit || "—"}</Td>
                <Td align="right" className="font-mono tabular">
                  {formatUnitPrice(it.unitPrice, d.currency)}
                </Td>
                <Td align="right" className="font-mono tabular">
                  {formatUnitPrice(it.amount, d.currency)}
                </Td>
              </Tr>
            ))}
          </tbody>
        </TableShell>
        <div className="flex justify-end p-4">
          <dl className="w-80 max-w-full space-y-2 text-sm">
            {[
              [t("common.subtotal"), formatUnitPrice(subtotal, d.currency)],
              [t("pi.upcharge"), formatUnitPrice(d.upcharge, d.currency)],
              [
                t("invoiceWorkspace.roundedBase"),
                formatMoney(base, d.currency),
              ],
              [
                `${t("pi.discountPct")} · ${d.discount / 100}%`,
                formatMoney(-less, d.currency),
              ],
              [
                `${t("pi.total")} · ${d.currency}`,
                formatMoney(d.totalAmount, d.currency),
              ],
            ].map(([label, value]) => (
              <div key={label} className="flex justify-between gap-3">
                <dt>{label}</dt>
                <dd className="font-mono tabular">{value}</dd>
              </div>
            ))}
          </dl>
        </div>
        {d.totalAmount !== piTotalMinor(subtotal, d.upcharge, d.discount) && (
          <p role="alert" className="px-4 pb-4 text-xs text-warn">
            {t("invoiceWorkspace.totalMismatch")}
          </p>
        )}
      </Panel>
      {d.remarks && (
        <Panel className="p-4">
          <p className="text-2xs uppercase text-ink-faint">
            {t("common.remarks")}
          </p>
          <p className="mt-1 whitespace-pre-line text-sm">{d.remarks}</p>
        </Panel>
      )}
      {d.paymentBank && (
        <Panel className="p-4">
          <p className="mb-3 text-2xs uppercase text-ink-faint">
            {t("invoiceWorkspace.bankSnapshot")}
          </p>
          <dl className="grid gap-3 text-xs sm:grid-cols-2">
            {Object.entries(d.paymentBank)
              .filter(([k]) => k !== "logoUrl")
              .map(([key, value]) => (
                <div key={key}>
                  <dt className="text-ink-faint">
                    {t(`invoiceWorkspace.bank_${key}`)}
                  </dt>
                  <dd className="mt-1 whitespace-pre-line font-mono tabular">
                    {value || "—"}
                  </dd>
                </div>
              ))}
          </dl>
        </Panel>
      )}
    </div>
  );
}
